import { ChangeEvent, FormEvent, useState } from "react"
import { toast } from "react-toastify"
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import { Button } from "~/ui-rtk/components/ui/controls"
import {
  type ArtifactVisibility,
  type CompanyArtifact,
  type PublishCompanyArtifactResult,
  usePublishCompanyArtifactMutation,
} from "~/ui-rtk/api/companyArtifactApi"

/** Mirrors the API cap (MAX_ARTIFACT_HTML_BYTES). */
const MAX_HTML_BYTES = 5 * 1024 * 1024

const inputClass =
  "w-full rounded-md bg-basic-black border border-basic-blue px-3 py-2 text-sm text-white placeholder:text-system-gray-500 focus:outline-none focus:border-system-blue-500"

type Props = {
  isOpen: boolean
  onClose: () => void
  /** Publish a new version of this artifact; omit to create a new one. */
  artifact?: CompanyArtifact
  onPublished?: (result: PublishCompanyArtifactResult) => void
}

function apiErrorMessage(error: unknown): string {
  const data = (error as { data?: { message?: string | string[] } })?.data
  const message = Array.isArray(data?.message)
    ? data?.message.join(" ")
    : data?.message
  return message || "Could not publish the artifact. Please try again."
}

/**
 * Agency-only: publish a Claude artifact (standalone HTML) as a new artifact
 * or a new version. Closes on ✕, outside click and Escape.
 */
export default function PublishArtifactDialog({
  isOpen,
  onClose,
  artifact,
  onPublished,
}: Props) {
  const isNewVersion = Boolean(artifact)
  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  const [html, setHtml] = useState("")
  const [fileName, setFileName] = useState<string | null>(null)
  const [sourceUrl, setSourceUrl] = useState("")
  const [note, setNote] = useState("")
  const [visibility, setVisibility] = useState<ArtifactVisibility>("PRIVATE")
  const [publish, { isLoading }] = usePublishCompanyArtifactMutation()

  const sizeBytes = new Blob([html]).size
  const tooBig = sizeBytes > MAX_HTML_BYTES
  const canSubmit =
    html.trim().length > 0 && !tooBig && (isNewVersion || title.trim())

  const reset = () => {
    setTitle("")
    setDescription("")
    setHtml("")
    setFileName(null)
    setSourceUrl("")
    setNote("")
    setVisibility("PRIVATE")
  }

  const handleClose = () => {
    if (isLoading) return
    reset()
    onClose()
  }

  const handleFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    setHtml(await file.text())
    setFileName(file.name)
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!canSubmit) return
    try {
      const result = await publish({
        ...(artifact
          ? { artifactId: artifact.id }
          : { title: title.trim(), visibility }),
        ...(description.trim() ? { description: description.trim() } : {}),
        html,
        ...(sourceUrl.trim() ? { sourceUrl: sourceUrl.trim() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      }).unwrap()
      toast.success(
        isNewVersion
          ? `Published version ${result.currentVersion}`
          : "Artifact published",
      )
      result.warnings.forEach(warning => toast.warn(warning))
      reset()
      onPublished?.(result)
      onClose()
    } catch (error) {
      toast.error(apiErrorMessage(error))
    }
  }

  return (
    <Dialog
      isOpen={isOpen}
      handleClose={handleClose}
      closeOnClickOutside
      width={640}
      classes={{
        container:
          "bg-basic-dark-blue border border-basic-blue rounded-2xl p-0 flex flex-col max-w-2xl w-full max-h-[90vh] overflow-hidden text-left",
      }}
    >
      <form onSubmit={handleSubmit} className="flex flex-col min-h-0">
        <div className="flex items-center justify-between gap-4 border-b border-white/10 p-5">
          <h2 className="text-lg font-bold text-white">
            {isNewVersion
              ? `New version of “${artifact?.title}”`
              : "Publish artifact"}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-400 transition-colors hover:text-white"
            aria-label="Close"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="flex flex-col gap-4 p-5 overflow-y-auto">
          {!isNewVersion && (
            <label className="flex flex-col gap-1 text-sm text-system-gray-300">
              Title
              <input
                className={inputClass}
                value={title}
                maxLength={200}
                onChange={e => setTitle(e.target.value)}
                placeholder="Q4 media plan"
                autoFocus
              />
            </label>
          )}
          {!isNewVersion && (
            <label className="flex flex-col gap-1 text-sm text-system-gray-300">
              Description (optional)
              <input
                className={inputClass}
                value={description}
                maxLength={2000}
                onChange={e => setDescription(e.target.value)}
              />
            </label>
          )}

          {!isNewVersion && (
            <label className="flex flex-col gap-1 text-sm text-system-gray-300">
              Who can see it
              <select
                className={inputClass}
                value={visibility}
                onChange={e =>
                  setVisibility(e.target.value as ArtifactVisibility)
                }
              >
                <option value="PRIVATE">
                  Private: only this company&apos;s users
                </option>
                <option value="PUBLIC">
                  Public: anyone with the link, no login
                </option>
              </select>
            </label>
          )}

          <div className="flex flex-col gap-1 text-sm text-system-gray-300">
            <span>HTML page</span>
            <label className="cursor-pointer self-start rounded-md border border-basic-blue px-3 py-1.5 text-white hover:bg-white/5">
              Upload .html file
              <input
                type="file"
                accept=".html,.htm,text/html"
                className="hidden"
                onChange={handleFile}
                data-testid="artifact-file-input"
              />
            </label>
            {fileName && (
              <span className="text-xs text-system-gray-400">{fileName}</span>
            )}
            <textarea
              className={`${inputClass} h-48 font-mono text-xs`}
              value={html}
              onChange={e => {
                setHtml(e.target.value)
                setFileName(null)
              }}
              placeholder="…or paste the full HTML of the artifact"
              aria-label="HTML"
            />
            <span
              className={`text-xs ${tooBig ? "text-error" : "text-system-gray-500"}`}
            >
              {(sizeBytes / 1024).toFixed(0)} KB of 5 MB. HTML only (export
              React artifacts as HTML). The page runs sandboxed: no network
              calls, no remote images, scripts only inline or from cdnjs /
              Tailwind CDN.
            </span>
          </div>

          <label className="flex flex-col gap-1 text-sm text-system-gray-300">
            Source link (optional)
            <input
              className={inputClass}
              value={sourceUrl}
              type="url"
              onChange={e => setSourceUrl(e.target.value)}
              placeholder="https://claude.ai/…"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-system-gray-300">
            Version note (optional)
            <input
              className={inputClass}
              value={note}
              maxLength={2000}
              onChange={e => setNote(e.target.value)}
              placeholder="What changed"
            />
          </label>
        </div>

        <div className="flex justify-end gap-3 border-t border-white/10 p-5">
          <Button
            size="sm"
            variant={{ variant: "outlined" }}
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            type="submit"
            variant={{ variant: "solid", color: "pink" }}
            isLoading={isLoading}
            disabled={!canSubmit}
          >
            {isNewVersion ? "Publish version" : "Publish"}
          </Button>
        </div>
      </form>
    </Dialog>
  )
}
